import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  // You can define any props your navbar component might receive
}

const Navbar: React.FC<NavbarProps> = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  // If not logged in or no profile yet, show nothing (or you could just remove the return null to always show the same navbar).
  if (!user || !profile) {
    return null;
  }

  const role = profile.role;

  return (
    <nav className="bg-indigo-700 text-white shadow-md" data-headlessui-state="">
      <div className="container max-w-7xl sm:px-6 lg:px-8 mx-auto flex items-center justify-between h-16 px-4">
        <div className="flex items-center">
          <div className="mr-6">
            <div className="flex items-center">
              <span className="text-2xl font-extrabold tracking-tight">
                <span className="text-indigo-300">Skill</span>
                <span className="text-white">Vista</span>
              </span>
            </div>
          </div>
          {/* Desktop menu */}
          <div className="hidden md:flex space-x-4">
            {/* Only administrator can see Administration link */}
            {role === 'administrator' && (
              <a
                href="/administration"
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
              >
                Administration
              </a>
            )}

            {/* Coach or Administrator can see Coach Portal */}
            {(role === 'administrator' || role === 'coach') && (
              <a
                href="/coach"
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
              >
                Coach Portal
              </a>
            )}

            {/* Parent, Coach, Administrator can see Parent Portal */}
            {(role === 'administrator' || role === 'coach' || role === 'parent') && (
              <a
                href="/parent"
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
              >
                Parent Portal
              </a>
            )}
          </div>

          {/* Hamburger Menu Button - visible on mobile */}
          <div className="md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
                data-slot="icon"
                className="h-6 w-6"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Right side icons (e.g., notifications, user avatar, sign out) */}
        <div className="flex items-center">
          <button
            type="button"
            className="relative rounded-full p-2 hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
          >
            <span className="sr-only">View notifications</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              aria-hidden="true"
              data-slot="icon"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="ml-3 p-2 rounded-full hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
          >
            <span className="sr-only">Open user profile</span>
            <User className="h-6 w-6 text-white" />
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="ml-3 p-2 rounded-full hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
          >
            <span className="sr-only">Sign out</span>
            <LogOut className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu items */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pb-3 space-y-1">
          {role === 'administrator' && (
            <a
              href="/administration"
              className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
            >
              Administration
            </a>
          )}

          {(role === 'administrator' || role === 'coach') && (
            <a
              href="/coach"
              className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
            >
              Coach Portal
            </a>
          )}

          {(role === 'administrator' || role === 'coach' || role === 'parent') && (
            <a
              href="/parent"
              className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
            >
              Parent Portal
            </a>
          )}

          <a
            href="/profile"
            className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
          >
            Profile
          </a>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-500 transition-colors duration-200"
          >
            Sign Out
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;